import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title, Text, Button, Card } from '@tremor/react';
import { ScaleIcon, PlusIcon, TrashIcon, CheckCircleIcon, ClockIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import { listBeAnalyses, createBeAnalysis, deleteBeAnalysis } from '../../Requests/BehavioralEconomicsRequests';
import UpgradedLoader from '../UpgradedLoader';
import BehavioralEconomicsModal from './BehavioralEconomicsModal';

/**
 * "Economia Comportamental" sub-tab: a list of saved analyses (cards). Each card is one full
 * run (biases + report + insights). Create as many as you want; open a card to consult/edit.
 */
function BeAnalysesTab({ projectUuid }) {
    const { t } = useTranslation();
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [openUuid, setOpenUuid] = useState(null); // analysis open in the modal

    const load = () => listBeAnalyses(projectUuid)
        .then((res) => setAnalyses(res.data.data ?? []))
        .finally(() => setLoading(false));

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectUuid]);

    const handleCreate = async () => {
        setCreating(true);
        try {
            const res = await createBeAnalysis(projectUuid);
            setAnalyses((prev) => [res.data, ...prev]);
            setOpenUuid(res.data.uuid); // open the new one right away
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (e, uuid) => {
        e.stopPropagation();
        if (!window.confirm(t('BehavioralEconomics.DeleteConfirm'))) return;
        await deleteBeAnalysis(projectUuid, uuid);
        setAnalyses((prev) => prev.filter((a) => a.uuid !== uuid));
    };

    const partIcon = (status) => {
        if (status === 'completed') return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
        if (status === 'processing') return <ClockIcon className="w-4 h-4 text-amber-500" />;
        return <MinusCircleIcon className="w-4 h-4 text-gray-300" />;
    };

    if (loading) return <UpgradedLoader />;

    return (
        <div data-testid="be-analyses-tab">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                    <ScaleIcon className="h-6 w-6 text-iavisus-100" />
                    <Title className="text-lg font-semibold">{t('BehavioralEconomics.TabName')}</Title>
                </div>
                <Button icon={PlusIcon} onClick={handleCreate} loading={creating} className="bg-iavisus-900">
                    {t('BehavioralEconomics.NewAnalysis')}
                </Button>
            </div>
            <Text className="mb-6 max-w-2xl">{t('BehavioralEconomics.Intro')}</Text>

            {analyses.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-lg p-10 text-center">
                    <ScaleIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <Text className="text-gray-500">{t('BehavioralEconomics.Empty')}</Text>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {analyses.map((a, i) => (
                        <Card
                            key={a.uuid}
                            onClick={() => setOpenUuid(a.uuid)}
                            className="p-4 cursor-pointer hover:ring-2 hover:ring-iavisus-100 transition"
                            data-testid="be-analysis-card"
                        >
                            <div className="flex items-start justify-between">
                                <Title className="text-base font-bold text-iavisus-100">
                                    {a.title || `${t('BehavioralEconomics.TabName')} #${analyses.length - i}`}
                                </Title>
                                <button onClick={(e) => handleDelete(e, a.uuid)} aria-label={t('BehavioralEconomics.Close')}>
                                    <TrashIcon className="w-4 h-4 text-gray-400 hover:text-red-500" />
                                </button>
                            </div>
                            {a.created_at && (
                                <Text className="text-xs text-gray-400 mt-1">
                                    {new Date(a.created_at).toLocaleDateString()}
                                </Text>
                            )}
                            <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
                                <span className="flex items-center gap-1">{partIcon(a.parts?.biases)} {t('BehavioralEconomics.Biases.TabName')}</span>
                                <span className="flex items-center gap-1">{partIcon(a.parts?.report)} {t('BehavioralEconomics.Report.TabName')}</span>
                                <span className="flex items-center gap-1">{partIcon(a.parts?.insights)} {t('BehavioralEconomics.Insights.TabName')}</span>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <BehavioralEconomicsModal
                isOpen={!!openUuid}
                onClose={() => { setOpenUuid(null); load(); }}
                projectUuid={projectUuid}
                analysisUuid={openUuid}
            />
        </div>
    );
}

export default BeAnalysesTab;
